import type { BookingSessionResponse, SubmitInsuranceRequest } from '@massilia/queries'
import type { UseMutateAsyncFunction } from '@tanstack/react-query'

import { countBillablePassengers } from '@massilia/queries'
import { Button, cn, Label, RadioGroup, RadioGroupItem, Skeleton } from '@massilia/ui'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Check, ChevronLeft, ExternalLink, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { BookingStepActions } from '@/components/booking/booking-step-actions'
import { PromoCodeSlot } from '@/components/booking/promo-code-slot'
import { TripHeader } from '@/components/booking/trip-header'
import { usePromoCode } from '@/components/booking/use-promo-code'

import { formatPrice } from './utils'

interface AssurancesFormProps {
  sessionId: string
  isLoadingEstimate: boolean
  isSubmitting: boolean
  session?: BookingSessionResponse
  submitInsurance: UseMutateAsyncFunction<
    BookingSessionResponse,
    Error,
    SubmitInsuranceRequest,
    unknown
  >
}

export function AssurancesForm({
  sessionId,
  isLoadingEstimate,
  isSubmitting,
  session,
  submitInsurance,
}: AssurancesFormProps) {
  const navigate = useNavigate()

  // Pre-fill from session: if user already decided insurance (back-nav), restore their choice
  const [selectedInsurance, setSelectedInsurance] = useState<'with' | 'without'>(
    session?.insurance?.withInsurance ? 'with' : 'without'
  )

  // Insurance estimate — fetched on page load via useFetchInsuranceEstimate
  const insuranceEstimate = session?.insuranceEstimate
  const insuranceOption = insuranceEstimate?.option ?? null
  const insurancePriceCents = insuranceEstimate?.priceCents ?? 0
  const insurancePrice = insurancePriceCents / 100
  const hasInsurance = insuranceOption !== null && insurancePrice > 0
  const isInsuranceUnavailable = !isLoadingEstimate && !hasInsurance

  const [insuranceError, setInsuranceError] = useState(false)

  // Promo code: applied at a previous step but must remain visible/editable here.
  const {
    isVerifying: isPromoCodePending,
    promoCodeError,
    remove: removePromoCode,
    verify: verifyPromoCode,
  } = usePromoCode(sessionId)

  const promoCodeDiscount = session?.promoCode ? session.promoCode.discountCents / 100 : null
  const billablePassengerCount = countBillablePassengers(session?.flightDetails.passengersCount)

  const handleBack = () => {
    void navigate({
      search: { session: sessionId },
      to: '/informations',
    })
  }

  const handleNext = async () => {
    // Submit the insurance decision to the server, then navigate to checkout
    try {
      await submitInsurance({ withInsurance: selectedInsurance === 'with' })
      void navigate({
        search: { session: sessionId },
        to: '/paiement',
      })
    } catch {
      setInsuranceError(true)
    }
  }

  return (
    <div className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12">
      {/* Mobile-only TripHeader */}
      {session && (
        <div className="lg:hidden">
          <TripHeader session={session} />
        </div>
      )}

      {/* Right column — Sidebar */}
      <div className="w-full shrink-0 lg:order-last lg:w-96">
        <BookingSidebar
          insurancePriceLoading={isLoadingEstimate && selectedInsurance === 'with'}
          isLoading={!session?.priceData}
          overrideInsurancePrice={selectedInsurance === 'with' ? insurancePrice : 0}
          promoCodeDiscount={promoCodeDiscount}
          promoCodeSlot={
            <PromoCodeSlot
              appliedCode={session?.promoCode?.code ?? null}
              discountEuros={promoCodeDiscount}
              error={promoCodeError}
              isVerifying={isPromoCodePending}
              onRemove={removePromoCode}
              onVerify={verifyPromoCode}
              passengerCount={billablePassengerCount}
            />
          }
          session={session}
        />
      </div>

      {/* Left column — Insurance choice */}
      <div className="flex flex-1 flex-col gap-9">
        {/* Desktop-only TripHeader */}
        {session && (
          <div className="hidden lg:block">
            <TripHeader session={session} />
          </div>
        )}

        {/* Insurance section title */}
        <div className="flex flex-col gap-2">
          <h2 className="text-gray-light-900 text-lg font-semibold">Voyagez en toute confiance</h2>
          <p className="text-gray-light-900 text-sm">
            L&apos;inattendu peut arriver. Ajoutez une couverture à votre voyage pour les imprévus
            et partez sereinement, quelles que soient les circonstances.
          </p>
        </div>

        {/* Insurance error */}
        {insuranceError && (
          <div className="border-warning-300 bg-warning-50 flex items-start gap-3 rounded-xl border p-4">
            <AlertTriangle className="text-warning-600 mt-0.5 size-5 shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="text-gray-light-900 text-sm font-semibold">
                Service d&apos;assurance indisponible
              </p>
              <p className="text-gray-light-700 text-sm">
                Nous n&apos;avons pas pu récupérer les offres d&apos;assurance. Vous pouvez
                continuer sans assurance ou réessayer plus tard.
              </p>
            </div>
          </div>
        )}

        {/* Insurance radio options */}
        <div className="flex w-full flex-col gap-4">
          <RadioGroup
            className="flex flex-col gap-0"
            onValueChange={(value) => {
              if (value === 'with' || value === 'without') {
                setSelectedInsurance(value)
              }
            }}
            value={selectedInsurance}
          >
            {/* Without insurance */}
            <div
              className={cn(
                'border-gray-light-300 cursor-pointer rounded-t-2xl border-t border-r border-l transition-all',
                selectedInsurance === 'without' && 'border-brand-600 bg-brand-50 border-b'
              )}
              onClick={() => setSelectedInsurance('without')}
            >
              <div className="flex items-start gap-2 p-4">
                <RadioGroupItem className="mt-0.5" id="without" value="without" />
                <div className="flex-1">
                  <Label className="flex-1 cursor-pointer text-sm font-semibold" htmlFor="without">
                    Sans assurance
                  </Label>
                </div>
                <div className="text-gray-light-900 text-sm font-semibold">0,00 &euro;</div>
              </div>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  selectedInsurance === 'without' ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                <div className="px-4 pb-4">
                  <p className="text-gray-light-700 text-sm">
                    Votre vol n&apos;est pas assuré au-delà des conditions de
                    modification/annulation
                  </p>
                </div>
              </div>
            </div>

            {/* With insurance */}
            <div
              className={cn(
                'border-gray-light-300 cursor-pointer rounded-b-2xl border-r border-b border-l transition-all',
                selectedInsurance === 'with' && 'border-brand-600 bg-brand-50 border-t',
                (insuranceError || isInsuranceUnavailable) && 'pointer-events-none opacity-50'
              )}
              onClick={() => {
                if (!insuranceError && hasInsurance && !isLoadingEstimate)
                  setSelectedInsurance('with')
              }}
            >
              <div className="flex items-start gap-2 p-4">
                <RadioGroupItem
                  className="mt-0.5"
                  disabled={insuranceError || isInsuranceUnavailable || isLoadingEstimate}
                  id="with"
                  value="with"
                />
                <div className="flex-1">
                  <Label className="flex-1 cursor-pointer text-sm font-semibold" htmlFor="with">
                    Avec assurance multirisques
                  </Label>
                </div>
                <div className="text-gray-light-900 text-sm font-semibold">
                  {isLoadingEstimate || isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : hasInsurance ? (
                    `+ ${formatPrice(insurancePrice)} \u20AC`
                  ) : (
                    '---'
                  )}
                </div>
              </div>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  selectedInsurance === 'with' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                {insuranceOption && (
                  <div className="flex w-full flex-col gap-3 px-4 pb-4">
                    {/* Formula name */}
                    <div className="flex items-center gap-2">
                      <Check className="text-success-600 size-4 shrink-0" />
                      <p className="text-gray-light-900 text-sm">{insuranceOption.nomFormule}</p>
                    </div>

                    {/* Extensions */}
                    {insuranceOption.extensions.map((ext) => (
                      <div className="flex items-center gap-2" key={ext.id}>
                        <Check className="text-success-600 size-4 shrink-0" />
                        <p className="text-gray-light-900 text-sm">{ext.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>

          <div className="mt-2 flex flex-col gap-2">
            <p className="text-gray-light-900 text-sm font-semibold">
              Qu&apos;est ce qui est couvert par l&apos;assurance ?
            </p>
            {isLoadingEstimate ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-4 w-60" />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {insuranceOption?.dinLink && (
                  <a
                    className="text-brand-600 hover:text-brand-700 inline-flex items-center gap-1.5 text-xs font-medium underline"
                    href={insuranceOption.dinLink}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <ExternalLink className="size-3 shrink-0" />
                    Documents d&apos;informations
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation buttons */}
        <BookingStepActions>
          <Button className="gap-2" onClick={handleBack} type="button" variant="ghost">
            <ChevronLeft className="text-brand-700 size-4" />
            <span className="text-brand-700 text-sm font-medium">Étape précédente</span>
          </Button>
          <Button
            className="bg-brand-600 hover:bg-brand-700 w-48 gap-2"
            disabled={isSubmitting}
            onClick={() => {
              void handleNext()
            }}
            type="button"
          >
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin text-white" />
            ) : (
              <span className="text-sm font-medium text-white">Payer</span>
            )}
          </Button>
        </BookingStepActions>
      </div>
    </div>
  )
}
